/**
 * Outbox (Phase 16C C3, design §6.6). Encrypted store `outbox`, key = opId (a ULID, also the
 * Idempotency-Key on the server).
 *
 * - `seq` comes from a per-device counter in `meta`.
 * - `dependsOn` is set automatically: an op depends on the latest unapplied op for the same entity
 *   (an update on its create), else on the visit's unapplied VISIT_START; CHART_SUBMIT depends on
 *   the visit's last chart op.
 * - Autosave coalescing: an update for an entity whose latest op is a not-yet-sent update or create
 *   of the same entity is merged into that op (newest payload, oldest expectedVersion), so
 *   30-second assessment autosaves don't create hundreds of ops.
 * - Each op is sealed to the tenant escrow key (escrow written before the op, so an op never exists
 *   without its sealed copy). Escrow entries are deleted when the op is applied or a duplicate.
 */
import type { EscrowKeyDto, SealedOpDto, SyncOpDto, SyncOpType, TimeAnchorDto } from '../../dtos/offline/sync.dto';
import { ulid } from './ids';
import { sealOp } from './escrow';
import type { OfflineRuntime } from './runtime';
import type { BlobRepo } from './repos/blobRepo';

export type OutboxStatus = 'pending' | 'sending' | 'applied' | 'duplicate' | 'conflict' | 'needs_attention';

export interface OutboxOp {
  opId: string;
  type: SyncOpType;
  userId: string;
  tenantId: string;
  patientId: string;
  visitId: string;
  entityId?: string;
  dependsOn?: string;
  seq: number;
  capturedAt: string;
  monotonicMs: number;
  timeAnchor?: TimeAnchorDto;
  expectedVersion?: number;
  observedAt?: string;
  payload: Record<string, unknown>;
  status: OutboxStatus;
  attempts: number;
  lastError?: string;
  errorCode?: string;
  /** The server's copy, for a conflict */
  current?: Record<string, unknown> | null;
  /** Shift id (VISIT_START) for the heartbeat's pendingShiftIds */
  shiftId?: string;
  createdAt: string;
}

export interface EnqueueInput {
  type: SyncOpType;
  patientId: string;
  visitId: string;
  entityId?: string;
  payload: Record<string, unknown>;
  expectedVersion?: number;
  observedAt?: string;
  /** Defaults to now */
  capturedAt?: string;
  /** Reuse an id (an online request that timed out is queued under its Idempotency-Key) */
  opId?: string;
  shiftId?: string;
}

export const OUTBOX_CHANGED_EVENT = 'offline-outbox-changed';
const SEQ_KEY = 'outboxSeq';
const ANCHOR_KEY = 'timeAnchor';
const ESCROW_KEY = 'escrowKey';

const COALESCE: ReadonlySet<SyncOpType> = new Set(['CHART_UPDATE', 'ASSESSMENT_UPDATE']);
const CREATE_FOR: Partial<Record<SyncOpType, SyncOpType>> = { CHART_UPDATE: 'CHART_CREATE', ASSESSMENT_UPDATE: 'ASSESSMENT_CREATE' };
const CHART_OPS: ReadonlySet<SyncOpType> = new Set(['CHART_CREATE', 'CHART_UPDATE']);

/** Ops not yet confirmed by the server (applied/duplicate ops are deleted). */
export const isUnsynced = (op: OutboxOp) => op.status !== 'applied' && op.status !== 'duplicate';

const monotonicNow = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? Math.round((performance.timeOrigin || 0) + performance.now())
    : Date.now();

export class Outbox {
  private listeners = new Set<() => void>();
  /** visitIds with queued ops, refreshed on every change (sync check for OfflineMode.shouldQueue) */
  private queuedVisits: Set<string> | null = null;

  constructor(private readonly rt: OfflineRuntime, private readonly blobs: BlobRepo) {}

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  private changed(): void {
    this.queuedVisits = null;
    this.listeners.forEach((l) => l());
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(OUTBOX_CHANGED_EVENT));
  }

  async list(): Promise<OutboxOp[]> {
    return (await this.rt.store().getAll<OutboxOp>('outbox')).map((r) => r.value).sort((a, b) => a.seq - b.seq);
  }

  get(opId: string): Promise<OutboxOp | undefined> {
    return this.rt.store().get<OutboxOp>('outbox', opId);
  }

  async hasQueuedForVisit(visitId: string): Promise<boolean> {
    if (!this.queuedVisits) this.queuedVisits = new Set((await this.list()).filter(isUnsynced).map((o) => o.visitId));
    return this.queuedVisits.has(visitId);
  }

  async counts(): Promise<{ pending: number; conflicts: number; attention: number; oldestPendingAt?: string; visitIds: string[]; shiftIds: string[] }> {
    const ops = (await this.list()).filter(isUnsynced);
    const visitIds = Array.from(new Set(ops.map((o) => o.visitId)));
    const shiftIds = Array.from(new Set(ops.filter((o) => o.type === 'VISIT_START' && o.shiftId).map((o) => o.shiftId as string)));
    return {
      pending: ops.length,
      conflicts: ops.filter((o) => o.status === 'conflict').length,
      attention: ops.filter((o) => o.status === 'needs_attention').length,
      oldestPendingAt: ops.map((o) => o.capturedAt).sort()[0],
      visitIds,
      shiftIds,
    };
  }

  // ---- meta (not PHI) ----

  async setEscrowKey(key: EscrowKeyDto | null): Promise<void> {
    if (key?.kid && key.spki) await this.rt.store().putMeta(ESCROW_KEY, key);
  }

  getEscrowKey(): Promise<EscrowKeyDto | undefined> {
    return this.rt.store().getMeta<EscrowKeyDto>(ESCROW_KEY);
  }

  async setTimeAnchor(anchor: TimeAnchorDto): Promise<void> {
    await this.rt.store().putMeta(ANCHOR_KEY, anchor);
  }

  getTimeAnchor(): Promise<TimeAnchorDto | undefined> {
    return this.rt.store().getMeta<TimeAnchorDto>(ANCHOR_KEY);
  }

  private async nextSeq(): Promise<number> {
    const seq = ((await this.rt.store().getMeta<number>(SEQ_KEY)) || 0) + 1;
    await this.rt.store().putMeta(SEQ_KEY, seq);
    return seq;
  }

  // ---- enqueue ----

  async enqueue(input: EnqueueInput): Promise<OutboxOp> {
    const ops = await this.list();
    const unsynced = ops.filter(isUnsynced);
    const forEntity = input.entityId ? unsynced.filter((o) => o.entityId === input.entityId) : [];
    const latestForEntity = forEntity[forEntity.length - 1];

    // Coalesce into a not-yet-sent update (or the create) of the same entity
    if (COALESCE.has(input.type) && latestForEntity && latestForEntity.status === 'pending' && latestForEntity.attempts === 0
      && (latestForEntity.type === input.type || latestForEntity.type === CREATE_FOR[input.type])) {
      const merged: OutboxOp = {
        ...latestForEntity,
        payload: latestForEntity.type === input.type ? input.payload : { ...latestForEntity.payload, ...input.payload },
        observedAt: input.observedAt ?? latestForEntity.observedAt,
        capturedAt: input.capturedAt || new Date(this.rt.now()).toISOString(),
      };
      await this.write(merged);
      return merged;
    }

    const identity = this.rt.identity();
    const visitStart = unsynced.find((o) => o.type === 'VISIT_START' && o.visitId === input.visitId);
    let dependsOn: string | undefined;
    if (input.type === 'CHART_SUBMIT') {
      dependsOn = unsynced.filter((o) => o.visitId === input.visitId && CHART_OPS.has(o.type)).pop()?.opId ?? visitStart?.opId;
    } else if (input.type !== 'VISIT_START') {
      dependsOn = latestForEntity?.opId ?? visitStart?.opId;
    }

    const anchor = await this.getTimeAnchor();
    const op: OutboxOp = {
      opId: input.opId || ulid(this.rt.now()),
      type: input.type,
      userId: identity.userId,
      tenantId: identity.tenantId,
      patientId: input.patientId,
      visitId: input.visitId,
      ...(input.entityId ? { entityId: input.entityId } : {}),
      ...(dependsOn ? { dependsOn } : {}),
      seq: await this.nextSeq(),
      capturedAt: input.capturedAt || new Date(this.rt.now()).toISOString(),
      monotonicMs: monotonicNow(),
      ...(anchor ? { timeAnchor: anchor } : {}),
      ...(input.expectedVersion !== undefined ? { expectedVersion: input.expectedVersion } : {}),
      ...(input.observedAt ? { observedAt: input.observedAt } : {}),
      ...(input.shiftId ? { shiftId: input.shiftId } : {}),
      payload: input.payload,
      status: 'pending',
      attempts: 0,
      createdAt: new Date(this.rt.now()).toISOString(),
    };
    await this.write(op);
    return op;
  }

  /** Seal (when the tenant key is known), then write the op. */
  private async write(op: OutboxOp): Promise<void> {
    const key = await this.getEscrowKey();
    if (key) {
      const sealed = await sealOp(await this.toWire(op), key);
      await this.rt.store().putSealed(op.opId, sealed);
    }
    await this.rt.store().put('outbox', op.opId, op);
    this.changed();
  }

  /** The op as sent to the server: local fields dropped, the signature blob inlined. */
  async toWire(op: OutboxOp, expectedVersion?: number): Promise<SyncOpDto> {
    const payload = { ...op.payload };
    const blobId = typeof payload.signatureBlobId === 'string' ? payload.signatureBlobId : null;
    if (blobId) {
      delete payload.signatureBlobId;
      const blob = await this.blobs.get(blobId);
      if (blob) payload.signature = blob.dataUrl;
    }
    const ev = op.expectedVersion ?? expectedVersion;
    return {
      opId: op.opId,
      type: op.type,
      userId: op.userId,
      tenantId: op.tenantId,
      patientId: op.patientId,
      visitId: op.visitId,
      ...(op.entityId ? { entityId: op.entityId } : {}),
      ...(op.dependsOn ? { dependsOn: op.dependsOn } : {}),
      seq: op.seq,
      capturedAt: op.capturedAt,
      monotonicMs: op.monotonicMs,
      ...(op.timeAnchor ? { timeAnchor: op.timeAnchor } : {}),
      ...(ev !== undefined ? { expectedVersion: ev } : {}),
      ...(op.observedAt ? { observedAt: op.observedAt } : {}),
      payload,
    };
  }

  // ---- results ----

  async update(opId: string, patch: Partial<OutboxOp>): Promise<OutboxOp | undefined> {
    const op = await this.get(opId);
    if (!op) return undefined;
    const next = { ...op, ...patch };
    await this.rt.store().put('outbox', opId, next);
    this.changed();
    return next;
  }

  /** Applied or duplicate: the op and its sealed copy leave the device. */
  async remove(opId: string): Promise<void> {
    await this.rt.store().delete('outbox', opId);
    await this.rt.store().deleteSealed(opId);
    this.changed();
  }

  async sealed(): Promise<SealedOpDto[]> {
    return (await this.rt.store().getAllSealed<SealedOpDto>()).map((r) => r.value);
  }

  async removeSealed(opId: string): Promise<void> {
    await this.rt.store().deleteSealed(opId);
  }
}
